import React, { useState, useEffect, useCallback } from "react";
import useApiGet from "../hooks/getData";
import useApiPost from "../hooks/postData";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import TournamentDetail from "./TournamentDetail";
import RoomPicker from "./RoomPicker";
import { formatDateTimeLocal, formatDuration, StatusBadge } from "./tournamentUtils";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "live", label: "Live" },
  { value: "settling", label: "Settling" },
  { value: "ended", label: "Ended" },
  { value: "cancelled", label: "Cancelled" },
];

const DURATION_PRESETS = [
  { sec: 300, label: "5 min" },
  { sec: 600, label: "10 min" },
  { sec: 900, label: "15 min" },
  { sec: 1800, label: "30 min" },
  { sec: 3600, label: "1 hour" },
];

const emptyForm = {
  title: "",
  duration_sec: 900,
  start_now: true,
  starts_at: "", // datetime-local value (browser local time)
  room_ids: [],
};

const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

function TournamentList() {
  const { getData } = useApiGet();
  const { postData, loading: saving } = useApiPost();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [tournaments, setTournaments] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detailId, setDetailId] = useState(null);
  const [cancelId, setCancelId] = useState(null);

  const fetchTournaments = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const params = { page };
        if (status) params.status = status;
        const res = await getData("/tournaments/admin/list", params);
        const p = res?.data?.tournaments;
        setTournaments(p?.data || []);
        setPagination({
          current_page: p?.current_page || 1,
          last_page: p?.last_page || 1,
          total: p?.total || 0,
        });
      } catch (err) {
        if (!silent) toast.error(errMsg(err, "Failed to load tournaments."));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    // eslint-disable-next-line
    [page, status]
  );

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  // Keep statuses fresh (scheduled -> live -> ended) while this page is open.
  useEffect(() => {
    const t = setInterval(() => fetchTournaments(true), 10000);
    return () => clearInterval(t);
  }, [fetchTournaments]);

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) return toast.error("Please enter a title.");
    if (form.room_ids.length < 2) return toast.error("Select at least 2 rooms.");
    if (!form.duration_sec || Number(form.duration_sec) < 60)
      return toast.error("Duration must be at least 60 seconds.");

    const payload = {
      title: form.title.trim(),
      room_ids: form.room_ids,
      duration_sec: Number(form.duration_sec),
    };

    if (form.start_now) {
      payload.start_now = true;
    } else {
      if (!form.starts_at) return toast.error("Please pick a start time.");
      const d = new Date(form.starts_at);
      if (d.getTime() <= Date.now() + 5000) return toast.error("Start time must be in the future.");
      // Backend wants UTC ISO-8601 ending in Z.
      payload.starts_at = d.toISOString().replace(/\.\d{3}Z$/, "Z");
    }

    try {
      const res = await postData("/tournaments/admin/create", payload);
      if (res?.success) {
        toast.success(res.message || "Tournament created.");
        setCreateOpen(false);
        setPage(1);
        fetchTournaments();
        if (res?.data?.tournament?.id) setDetailId(res.data.tournament.id);
      } else {
        toast.error(res?.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(errMsg(err, "Failed to create tournament."));
    }
  };

  const handleCancel = async () => {
    try {
      const res = await postData(`/tournaments/admin/${cancelId}/cancel`, {});
      if (res?.success) {
        toast.success(res.message || "Tournament cancelled.");
        setCancelId(null);
        fetchTournaments();
      } else {
        toast.error(res?.message || "Failed to cancel.");
      }
    } catch (err) {
      toast.error(errMsg(err, "Failed to cancel."));
      setCancelId(null);
      fetchTournaments();
    }
  };

  const handleStart = async (id) => {
    try {
      const res = await postData(`/tournaments/admin/${id}/start`, {});
      if (res?.success) {
        toast.success(res.message || "Tournament started.");
      } else {
        toast.error(res?.message || "Failed to start.");
      }
    } catch (err) {
      toast.error(errMsg(err, "Failed to start."));
    }
    fetchTournaments();
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">
        <div className="flex items-center justify-between py-3">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl pt-3 dark:text-darkText">
            Tournaments
          </h2>
          <button
            style={{ backgroundColor: "#0D9947" }}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            onClick={openCreate}
          >
            + Create Tournament
          </button>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 pb-4">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => {
                setStatus(f.value);
                setPage(1);
              }}
              className={`px-4 py-1.5 text-xs rounded-full font-poppins border ${
                status === f.value
                  ? "bg-[#593A99] text-white border-[#593A99]"
                  : "text-[#593A99] border-[#593A99] dark:text-darkText"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : tournaments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <h2 className="text-sm font-semibold font-poppins text-[#000000] dark:text-darkText">
              Don't have any data to show
            </h2>
          </div>
        ) : (
          <div className="overflow-x-auto border border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
            <table className="min-w-full text-sm text-left font-poppins">
              <thead style={{ backgroundColor: "#593A99" }} className="text-white">
                <tr>
                  <th className="p-3">ID</th>
                  <th className="p-3">Title</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Rooms</th>
                  <th className="p-3">Duration</th>
                  <th className="p-3">Starts At</th>
                  <th className="p-3">Ends At</th>
                  <th className="p-3">Winner Room</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tournaments.map((t) => (
                  <tr key={t.id} className="border-t dark:border-[#1F1F1F] dark:text-darkText">
                    <td className="p-3">{t.id}</td>
                    <td className="p-3">{t.title}</td>
                    <td className="p-3">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="p-3">{t.participants_count}</td>
                    <td className="p-3">{formatDuration(t.duration_sec)}</td>
                    <td className="p-3">{formatDateTimeLocal(t.starts_at)}</td>
                    <td className="p-3">{formatDateTimeLocal(t.ends_at)}</td>
                    <td className="p-3">{t.winner_room_id || "-"}</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDetailId(t.id)}
                          className="px-3 py-1.5 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg"
                        >
                          View
                        </button>
                        {t.status === "scheduled" && (
                          <button
                            onClick={() => handleStart(t.id)}
                            disabled={saving}
                            className="px-3 py-1.5 text-xs font-poppins text-[#0D9947] border border-[#0D9947] rounded-lg disabled:opacity-40"
                          >
                            Start Now
                          </button>
                        )}
                        {(t.status === "scheduled" || t.status === "live") && (
                          <button
                            onClick={() => setCancelId(t.id)}
                            className="px-3 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.last_page > 1 && (
          <div className="flex items-center justify-end gap-3 pt-4 font-poppins dark:text-darkText">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-1.5 text-xs border border-header rounded-lg disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-sm">
              Page {pagination.current_page} / {pagination.last_page}
            </span>
            <button
              disabled={page >= pagination.last_page}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-1.5 text-xs border border-header rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create modal */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Create Tournament</DialogTitle>
        <DialogContent>
          <form onSubmit={handleCreate} className="flex flex-col gap-4 py-2">
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Title</label>
              <input
                type="text"
                maxLength={120}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. Friday Night Showdown"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">Duration</label>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {DURATION_PRESETS.map((d) => (
                  <button
                    type="button"
                    key={d.sec}
                    onClick={() => setForm({ ...form, duration_sec: d.sec })}
                    className={`px-3 py-1.5 text-xs rounded-lg font-poppins border ${
                      Number(form.duration_sec) === d.sec
                        ? "bg-[#593A99] text-white border-[#593A99]"
                        : "text-[#593A99] border-[#593A99] dark:text-darkText"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
                <input
                  type="number"
                  min={60}
                  max={86400}
                  value={form.duration_sec}
                  onChange={(e) => setForm({ ...form, duration_sec: e.target.value })}
                  className="w-32 border border-opacity-gradient rounded-lg p-1.5 text-sm dark:bg-transparent dark:text-darkText"
                />
                <span className="text-xs font-poppins dark:text-darkText">seconds (60 – 86400)</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">Start</label>
              <div className="flex items-center gap-4 pt-1 font-poppins text-sm dark:text-darkText">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={form.start_now}
                    onChange={() => setForm({ ...form, start_now: true })}
                  />
                  Start now
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={!form.start_now}
                    onChange={() => setForm({ ...form, start_now: false })}
                  />
                  Schedule
                </label>
              </div>
              {!form.start_now && (
                <input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  className="mt-2 border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                />
              )}
              {!form.start_now && (
                <p className="pt-1 text-xs text-[#666666] font-poppins dark:text-darkText">
                  Time is in your local timezone; it is sent to the server as UTC. Groups whose host is not
                  in the room at start time are dropped.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">
                Rooms ({form.room_ids.length} selected, min 2)
              </label>
              <RoomPicker
                selected={form.room_ids}
                onChange={(ids) => setForm({ ...form, room_ids: ids })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#593A99" }}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins disabled:opacity-60"
              >
                {saving ? "Saving..." : form.start_now ? "Create & Start" : "Schedule"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail modal (live scores, add/remove rooms) */}
      <TournamentDetail
        tournamentId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => fetchTournaments(true)}
      />

      {/* Cancel confirm */}
      <Dialog open={!!cancelId} onClose={() => setCancelId(null)}>
        <DialogTitle>Cancel Tournament?</DialogTitle>
        <DialogContent>
          <p className="pb-4 font-poppins dark:text-darkText">
            The tournament will end immediately. Coins already spent on gifts are not refunded.
          </p>
          <div className="flex justify-end gap-2 pb-2">
            <button
              onClick={() => setCancelId(null)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Keep
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              style={{ backgroundColor: "#D92D20" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins disabled:opacity-60"
            >
              Cancel Tournament
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TournamentList;
